/**
 * ISAYA — dynamic upsell row.
 *
 * Personalises the "You may also like" grid for the individual visitor, in the order that
 * actually moves average order value:
 *
 *   1. complements of what is already in the cart   — live buying intent, the strongest signal
 *   2. complements of the product being viewed      — the curated Search & Discovery list
 *   3. complements of recently viewed products
 *   4. store-wide best sellers                      — a brand-new visitor with an empty cart
 *   5. recently viewed products themselves          — last resort, so the row is never short
 *
 * Never shows the current product, anything already in the cart, or a sold-out product, and
 * never renders fewer cards than the section asks for while enough products exist.
 *
 * Cards are moved out of the server-rendered <template> pool, never built here, so a
 * personalised card is byte-identical to a server-rendered one.
 *
 * Every step is best-effort. Any failure leaves the server-rendered baseline on screen.
 */

const VIEWED_STORAGE_KEY = 'viewedProducts';

class IsayaRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.isayaDone) return;
    this.dataset.isayaDone = 'true';

    this.grid = this.querySelector('[data-isaya-grid]');
    this.pool = this.querySelector('[data-isaya-pool]');
    this.max = parseInt(this.dataset.max, 10) || 4;
    this.currentHandle = this.dataset.currentHandle;
    this.recommendationsUrl = this.dataset.recommendationsUrl || '/recommendations/products';

    if (!this.grid || !this.pool) return;

    this.available = new Map();
    for (const card of this.pool.content.querySelectorAll('[data-handle]')) {
      this.available.set(card.dataset.handle, card);
    }

    this.personalise().catch(() => {
      /* baseline stays on screen */
    });
  }

  async personalise() {
    const excluded = new Set([this.currentHandle]);

    const cart = await this.getCart();
    for (const handle of cart.handles) excluded.add(handle);

    const viewed = this.getViewed().filter((id) => String(id) !== String(this.dataset.currentId));

    /** @type {string[]} ordered candidate handles, best signal first */
    const ordered = [];

    // 1. Complements of what is in the cart, capped at half the row. The cart is the strongest
    //    signal, but it must not take the whole row — the visitor is still looking at *this*
    //    product, and without the cap every product page would show the same four.
    const fromCart = [];
    for (const id of cart.productIds) {
      fromCart.push(...(await this.complementsFor(id)));
    }
    ordered.push(...this.resolve(fromCart, excluded).slice(0, Math.floor(this.max / 2)));

    // 2. The curated list for the product being viewed — what the server already rendered.
    ordered.push(...this.baselineHandles());

    // 3. Complements of recently viewed products.
    for (const id of viewed) {
      if (this.enough(ordered, excluded)) break;
      ordered.push(...(await this.complementsFor(id)));
    }

    // 4. Store-wide best sellers, for a visitor we know nothing about.
    if (!this.enough(ordered, excluded)) {
      ordered.push(...(await this.bestSellers()));
    }

    // 5. Recently viewed products themselves — weak for order value, so they only ever fill.
    if (!this.enough(ordered, excluded)) {
      ordered.push(...(await this.handlesForIds(viewed)));
    }

    const picks = this.resolve(ordered, excluded);

    // Never leave the row shorter than the server already had it.
    if (picks.length < Math.min(this.max, this.available.size)) return;

    this.render(picks);
  }

  /** Handles currently rendered by the server, in order. */
  baselineHandles() {
    return Array.from(this.grid.querySelectorAll('[data-handle]')).map((el) => el.dataset.handle);
  }

  /** True once enough distinct, allowed handles have been collected. */
  enough(ordered, excluded) {
    return this.resolve(ordered, excluded).length >= this.max;
  }

  /** Order-preserving dedupe, dropping excluded and unavailable handles. */
  resolve(ordered, excluded) {
    const picks = [];
    for (const handle of ordered) {
      if (picks.length >= this.max) break;
      if (!handle || excluded.has(handle) || picks.includes(handle)) continue;
      if (!this.available.has(handle)) continue; // pool holds available products only
      picks.push(handle);
    }
    return picks;
  }

  render(handles) {
    const fragment = document.createDocumentFragment();
    for (const handle of handles) {
      fragment.appendChild(this.available.get(handle).cloneNode(true));
    }
    this.grid.replaceChildren(fragment);
  }

  async getCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) throw new Error('cart');
      const cart = await response.json();
      const items = cart.items || [];
      return {
        handles: items.map((item) => item.handle).filter(Boolean),
        // Most recently added first — the freshest intent leads.
        productIds: [...new Set(items.map((item) => item.product_id))].reverse(),
      };
    } catch {
      return { handles: [], productIds: [] };
    }
  }

  getViewed() {
    try {
      const stored = JSON.parse(localStorage.getItem(VIEWED_STORAGE_KEY) || '[]');
      return Array.isArray(stored) ? stored : [];
    } catch {
      return [];
    }
  }

  /** Curated complementary handles for a product id, straight from Search & Discovery. */
  async complementsFor(productId) {
    try {
      const url = `${this.recommendationsUrl}.json?product_id=${productId}&intent=complementary&limit=10`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('recommendations');
      const data = await response.json();
      return (data.products || []).map((product) => product.handle);
    } catch {
      return [];
    }
  }

  /** Store-wide best sellers. Shopify exposes the ordering on the collection page, not as JSON. */
  async bestSellers() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}collections/all?sort_by=best-selling`);
      if (!response.ok) throw new Error('best-selling');
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const handles = [];
      for (const link of doc.querySelectorAll('a[href*="/products/"]')) {
        const match = link.getAttribute('href').match(/\/products\/([a-z0-9-]+)/i);
        if (match && !handles.includes(match[1])) handles.push(match[1]);
      }
      return handles;
    } catch {
      return [];
    }
  }

  /** Recently viewed ids are ids, and the grid works in handles. */
  async handlesForIds(productIds) {
    if (!productIds.length) return [];
    try {
      const root = window.Shopify?.routes?.root || '/';
      const query = productIds.map((id) => `id:${id}`).join(' OR ');
      const url = `${root}search?type=product&q=${encodeURIComponent(query)}&view=`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('search');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const handles = [];
      for (const link of doc.querySelectorAll('a[href*="/products/"]')) {
        const match = link.getAttribute('href').match(/\/products\/([a-z0-9-]+)/i);
        if (match && !handles.includes(match[1])) handles.push(match[1]);
      }
      return handles;
    } catch {
      return [];
    }
  }
}

if (!customElements.get('isaya-recommendations')) {
  customElements.define('isaya-recommendations', IsayaRecommendations);
}
